import {
  canViewCompliance,
  userHasLevel1OnlyAccess
} from "@utils/projectAccess";
import * as RouteHelper from "@utils/paths";
import { getSectionVisibility } from "@shared/section-registry/sectionVisibility";
import { getVisibleEnvironmentalTabs } from "@domains/environmental";
import {
  EMISSIONS_TAB_TITLE,
  EnvironmentalTab
} from "@domains/environmental/constants/environmental";
import { PolicyCategory } from "catchment-common/entities/projectPolicy";
import { ContractorPackageLineItem } from "catchment-common/interfaces/contractorPackage";
import { frameworkAbbreviationFor } from "catchment-common/interfaces/framework";
import { ProjectLineItem } from "catchment-common/interfaces/project";
import { ProjectFrameworkLineItem } from "catchment-common/interfaces/projectFramework";
import { NavItemType } from "types";

import { safeUrl } from "./menuItemUrl";
import Icon from "@ui/atoms/Icon/Icon";
import { type IconName } from "@ui/atoms/Icon/registry";
import { Monogram } from "@ui/atoms/Monogram/Monogram";

const ENVIRONMENTAL_CATEGORY_ROUTES: Record<
  EnvironmentalTab,
  (projectId: string, contractorPackageId: string) => string
> = {
  [EnvironmentalTab.Energy]: RouteHelper.analyticsEnergyLevel1,
  [EnvironmentalTab.Materials]: RouteHelper.analyticsMaterialsLevel1,
  [EnvironmentalTab.Transport]: RouteHelper.analyticsTransportLevel1,
  [EnvironmentalTab.Waste]: RouteHelper.analyticsWasteLevel1,
  [EnvironmentalTab.Water]: RouteHelper.analyticsWaterLevel1
};

interface GovAssureResourceConfig {
  category: PolicyCategory;
  icon: IconName;
  key: string;
  label: string;
}

const GOVASSURE_RESOURCES: GovAssureResourceConfig[] = [
  {
    category: PolicyCategory.Document,
    icon: "blank-file",
    key: "documents",
    label: "Documents"
  },
  { category: PolicyCategory.Form, icon: "file", key: "forms", label: "Forms" },
  {
    category: PolicyCategory.Survey,
    icon: "list-checks",
    key: "surveys",
    label: "Surveys"
  },
  {
    category: PolicyCategory.Register,
    icon: "laptop",
    key: "registers",
    label: "Registers"
  }
];

const GOVASSURE_ROUTES: Record<
  string,
  (projectId: string, contractorPackageId: string) => string
> = {
  documents: RouteHelper.analyticsGovAssureDocuments,
  forms: RouteHelper.analyticsGovAssureForms,
  registers: RouteHelper.analyticsGovAssureRegisters,
  surveys: RouteHelper.analyticsGovAssureSurveys
};

const getGovAssureChildren = (
  project?: ProjectLineItem,
  contractorPackage?: ContractorPackageLineItem
): NavItemType[] => {
  const committedCategories = new Set(
    (project?.policies ?? [])
      .filter((policy) =>
        contractorPackage?.policies?.some(
          (packagePolicy) => packagePolicy.projectPolicyId === policy.id
        )
      )
      .map((policy) => policy.policyCategory)
  );

  return GOVASSURE_RESOURCES.filter((resource) =>
    committedCategories.has(resource.category)
  ).map((resource) => ({
    icon: <Icon name={resource.icon} />,
    id: `analytics-govassure-${resource.key}`,
    title: resource.label,
    type: "item",
    url: safeUrl(project, contractorPackage, () =>
      GOVASSURE_ROUTES[resource.key](project!.id, contractorPackage!.id)
    )
  }));
};

/**
 * EnviroTrack's children: Emissions always first (it has no
 * `EnvironmentalTargetType` and isn't gated by commitments), then the
 * project's committed categories — the same
 * {@link getVisibleEnvironmentalTabs} filter the old single-page shell used to
 * apply to its tab strip, now applied here so a sub-package viewer sees only
 * the categories their own package commits to, matching today's access rules.
 */
const getEnvironmentalChildren = (
  project?: ProjectLineItem,
  contractorPackage?: ContractorPackageLineItem
): NavItemType[] => [
  {
    icon: <Icon name="emission" />,
    id: "analytics-emissions",
    title: EMISSIONS_TAB_TITLE,
    type: "item",
    url: safeUrl(project, contractorPackage, () =>
      RouteHelper.analyticsEmissionsLevel1(project!.id, contractorPackage!.id)
    )
  },
  ...getVisibleEnvironmentalTabs(project, contractorPackage).map((tab) => ({
    icon: <Icon name={tab.icon} />,
    id: `analytics-${tab.value}`,
    title: tab.label,
    type: "item",
    url: safeUrl(project, contractorPackage, () =>
      ENVIRONMENTAL_CATEGORY_ROUTES[tab.value](
        project!.id,
        contractorPackage!.id
      )
    )
  }))
];

/**
 * The Compliance children: one per framework the project adopted in Set-Up.
 *
 * There is no configured list of "supported" frameworks — the sidebar IS the
 * project's compliance obligations, so it reads `projectFrameworks` directly.
 * A project with none gets no Compliance group at all (a childless group
 * renders nothing anyway, but the caller drops it explicitly so the intent is
 * readable).
 *
 * Each renders its catalogue abbreviation as a monogram rather than a shared
 * glyph. Every item used to be `file-check`, which is legible enough expanded
 * (the name is beside it) but reduces the collapsed rail to a column of
 * identical icons — and a project's frameworks are typically all from ONE
 * jurisdiction, so a crest per jurisdiction would not have separated them
 * either. The abbreviation is the only thing that differs per framework and
 * still fits.
 *
 * The CATALOGUE leads, and the project row's own abbreviation is the fallback
 * — a user-authored policy has no catalogue entry and carries the only copy
 * there is. A row with neither falls back to the shared glyph rather than
 * rendering an empty tile.
 *
 * Ids are `analytics-compliance-<frameworkId>` to match what
 * `resolveProjectSelectedItem` derives from the route's fourth segment —
 * without the suffix every framework would highlight at once.
 *
 * Deduped by framework id: `FrameworkId` is documented as globally unique and
 * a custom policy's `custom:<uuid>` cannot collide with one, so
 * two rows sharing one is a data fault rather than a case to render — and a
 * duplicate here would mean two children with the same React key and the same
 * highlight id.
 */
const getComplianceChildren = (
  project?: ProjectLineItem,
  contractorPackage?: ContractorPackageLineItem
): NavItemType[] => {
  const seen = new Set<string>();
  const frameworks = (project?.projectFrameworks ?? []).filter(
    (framework: ProjectFrameworkLineItem) => {
      if (seen.has(framework.frameworkId)) return false;
      seen.add(framework.frameworkId);
      return true;
    }
  );

  return frameworks.map((framework) => {
    // Catalogue first, then the row's own — a custom policy has no catalogue
    // entry, and its abbreviation is the only thing that separates one from
    // another in the collapsed rail.
    const abbreviation = frameworkAbbreviationFor(framework);

    return {
      icon: abbreviation ? (
        <Monogram label={abbreviation} />
      ) : (
        <Icon name="file-check" />
      ),
      id: `analytics-compliance-${framework.frameworkId}`,
      title: framework.frameworkName,
      type: "item",
      url: safeUrl(project, contractorPackage, () =>
        RouteHelper.analyticsCompliance(
          project!.id,
          contractorPackage!.id,
          framework.frameworkId
        )
      )
    };
  });
};

// The "overview" group.
//
// It used to take a `reduced` flag for the non-head-contractor variant, whose
// only effect was a distinct Local Content id — and nothing mapped a route to
// that id, so the item never highlighted. Both variants already built the same
// package-scoped URL, so with the id unified there is nothing left to vary:
// there is one package nav for both the head contract and its subpackages.
//
// The three social sections nest under a `SocialPro` group, the six
// environmental types under an `EnviroTrack` group, GovAssure's four resource
// types under their own group, and Compliance under its own — mirroring
// `frontend/src/domains/{social,environmental,governance}`.
//
// Level1Only (shared) viewers keep a FLAT list. That nav deliberately has no
// group captions (see the `title` at the bottom) — it is one list of places to
// look — so nesting inside it would introduce a hierarchy the surrounding nav
// doesn't have.
export const getPackageNav = (
  project?: ProjectLineItem,
  contractorPackage?: ContractorPackageLineItem,
  /** Whether the viewer's own package is the head contractor's. Gates
   * Compliance — see `canViewCompliance`. */
  isHeadContractorPackage = false
): NavItemType => {
  const children = [];
  const isFlatNav = userHasLevel1OnlyAccess(project);

  const {
    isDocumentsVisible,
    isEnvironmentalVisible,
    isLabourHoursVisible,
    isLocalContentVisible,
    isSocialSpendVisible
  } = getSectionVisibility(project, contractorPackage);

  // The full nav gives the dashboard its own Overview group (see
  // `getOverviewNav`). The shared (Level1Only) nav is one flat, caption-less
  // list, so splitting it there would invent a hierarchy the rest of that nav
  // does not have — its dashboard stays at the head of this list.
  if (isFlatNav) {
    children.push({
      icon: <Icon name="home" />,
      id: "dashboard",
      title: "Dashboard",
      type: "item",
      url: safeUrl(project, contractorPackage, () =>
        RouteHelper.contractorDashboard(project!.id, contractorPackage!.id)
      )
    });
  }

  const socialProChildren: NavItemType[] = [];

  if (isLabourHoursVisible) {
    socialProChildren.push({
      icon: <Icon name="pending" />,
      id: "analytics-labour-hours",
      title: "Participation",
      type: "item",
      url: safeUrl(project, contractorPackage, () =>
        RouteHelper.analyticsLabourHoursLevel1(
          project!.id,
          contractorPackage!.id
        )
      )
    });
  }

  if (isSocialSpendVisible) {
    socialProChildren.push({
      icon: <Icon name="dollar" />,
      id: "analytics-spend",
      title: "Certified Spend",
      type: "item",
      url: safeUrl(project, contractorPackage, () =>
        RouteHelper.analyticsSpendLevel1(project!.id, contractorPackage!.id)
      )
    });
  }

  if (isLocalContentVisible) {
    socialProChildren.push({
      icon: <Icon name="map-marker" />,
      // Both variants link to the same package-scoped route, so they share the
      // id the route resolves to (`resolveProjectSelectedItem` keys off the
      // first path segment). The reduced nav used to carry its own
      // `package-analytics-local-content`, which nothing mapped a route onto —
      // so on that nav the item never highlighted while you were on its page.
      id: "analytics-local-content",
      title: "Content Origin",
      type: "item",
      url: safeUrl(project, contractorPackage, () =>
        RouteHelper.analyticsLocalContent(project!.id, contractorPackage!.id)
      )
    });
  }

  if (socialProChildren.length > 0) {
    if (isFlatNav) {
      children.push(...socialProChildren);
    } else {
      children.push({
        children: socialProChildren,
        icon: <Icon name="webhook" />,
        id: "analytics-social-pro",
        title: "SocialPro",
        type: "item",
        url: socialProChildren[0].url
      });
    }
  }

  if (isEnvironmentalVisible) {
    const environmentalChildren = getEnvironmentalChildren(
      project,
      contractorPackage
    );
    if (isFlatNav) {
      children.push(...environmentalChildren);
    } else {
      children.push({
        children: environmentalChildren,
        icon: <Icon name="environmental" />,
        id: "analytics-environmental",
        title: "EnviroTrack",
        type: "item",
        url: environmentalChildren[0].url
      });
    }
  }

  // A package with policies always commits to at least one category, so the
  // length test is not expected to bite — but the group reads `[0].url`, so it
  // is guarded the same way Compliance's is rather than left to throw.
  const govAssureChildren = isDocumentsVisible
    ? getGovAssureChildren(project, contractorPackage)
    : [];

  if (govAssureChildren.length > 0) {
    if (isFlatNav) {
      children.push(...govAssureChildren);
    } else {
      children.push({
        children: govAssureChildren,
        icon: <Icon name="governance" />,
        id: "analytics-govassure",
        title: "GovAssure",
        type: "item",
        url: govAssureChildren[0].url
      });
    }
  }

  // Head contractors and clients only. A subcontractor's obligations are the
  // package targets they were given, not the project's standing against the
  // policies it adopted — and the analytics behind the page answer for their
  // package alone, so the section would not even be about what it claims to be.
  const complianceChildren = canViewCompliance(isHeadContractorPackage, project)
    ? getComplianceChildren(project, contractorPackage)
    : [];

  if (complianceChildren.length > 0) {
    if (isFlatNav) {
      children.push(...complianceChildren);
    } else {
      children.push({
        children: complianceChildren,
        icon: <Icon name="compliance" />,
        id: "analytics-compliance",
        title: "Compliance",
        type: "item",
        url: complianceChildren[0].url
      });
    }
  }

  // Forecasting reads as analytics for a Level1Only (shared) viewer: the project
  // forecast, read-only. Editors reach the same feature from Manage, where it
  // sits beside the package-scoped view they can also edit — so this entry is
  // only for the viewers who have no Manage group at all. It needs a target to
  // forecast against, hence the same visibility test the target sections use.
  if (
    isFlatNav &&
    (isLabourHoursVisible || isSocialSpendVisible || isLocalContentVisible)
  ) {
    children.push({
      icon: <Icon name="trending-up" />,
      id: "shared-forecast",
      title: "Forecasting",
      type: "item",
      url: safeUrl(project, contractorPackage, () =>
        RouteHelper.forecastProjectLevel1(project!.id)
      )
    });
  }

  return {
    children,
    id: "analytics",
    // The shared (Level1Only) nav is a single flat list of places to look —
    // there is no second kind of group to tell it apart from, so the caption
    // labels nothing. See `getReportsNav` for the other half of that nav.
    title: isFlatNav ? undefined : "analytics",
    type: "group"
  };
};
